import { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { parseErrorMessage } from '@/utils/parseErrorMessage';
import {
  ChevronLeft,
  Eye,
  Download,
  Trash2,
  UserPlus,
  Plus,
  CheckCircle2,
  RefreshCw,
  CloudUpload,
  XCircle,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { PodusCheckerNavigation } from '@/components/Navbar';
import SubmitReportModal from '@/components/modals/SubmitReportModal';
import ValidationModal from '@/components/modals/ValidationModal';
import {
  useGetInvestigationByIdQuery,
  useDeleteDocumentMutation,
  useGetAllUsersQuery,
  useAssignAgentMutation,
  useReassignInvestigationMutation,
  useValidateInvestigationReportMutation,
  useSubmitRiskAssessmentMutation,
  useGetRiskAssessmentQuery,
  useGetRiskAssessmentHistoryQuery,
  useGetFilteredDocumentsQuery,
  useUploadAgentDocumentMutation,
  useApproveDocumentMutation,
} from '@/store/api/apiSlice';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { CheckType, InvestigationStatus, UserRole, RiskAssessmentSubmission } from '@/types';

type TabType = 'details' | 'report';
type SubTabType = 'personal' | 'documents';
type EmployerReportSubTab = 'risk-score' | 'field-report';

// Bottom section tabs for personal details
const DETAILS_SECTION_TABS = [
  'Personal Identification',
  'Criminal & Law Enforcement Disclosure',
  'Financial & Directorship History',
  'Political Exposure',
  'Workplace Conduct',
  'Civil Claims',
  'Media Disclosure',
  'Conflict Of Interest',
  'Data Protection & Consent',
  'Professional Discipline',
] as const;

type PersonalDetailsSection = (typeof DETAILS_SECTION_TABS)[number];

// Risk assessment categories with checkbox options
const RISK_ASSESSMENT_CATEGORIES = [
  {
    title: 'Identity Integrity',
    category: 'identity_integrity',
    weight: 2,
    options: [
      { label: 'Fully verified, consistent', score: 0 },
      { label: 'Minor inconsistencies', score: 3 },
      { label: 'Identity manipulation or fraud indicators', score: 5 },
    ],
  },
  {
    title: 'Criminal & Anti-Corruption',
    category: 'criminal_anti_corruption',
    weight: 4,
    options: [
      { label: 'No criminal record', score: 0 },
      { label: 'Minor non-financial offence (historical)', score: 2 },
      { label: 'Past financial misconduct resolved', score: 3 },
      { label: 'Ongoing investigation', score: 4 },
      { label: 'Active prosecution or conviction', score: 5 },
    ],
  },
  {
    title: 'Financial & Directorship',
    category: 'financial_directorship',
    weight: 3,
    options: [
      { label: 'Clean record', score: 0 },
      { label: 'Company dissolved (legitimate)', score: 2 },
      { label: 'Multiple failed entities', score: 3 },
      { label: 'Undisclosed beneficial ownership', score: 4 },
      { label: 'Director disqualification/bankruptcy fraud', score: 5 },
    ],
  },
  {
    title: 'Regulatory & Discipline',
    category: 'regulatory_discipline',
    weight: 3,
    options: [
      { label: 'No disciplinary findings', score: 0 },
      { label: 'Past regulatory reprimand', score: 3 },
      { label: 'Licence revoked or severe sanction', score: 5 },
    ],
  },
  {
    title: 'Political Exposure',
    category: 'political_exposure',
    weight: 1,
    options: [
      { label: 'No PEP exposure', score: 0 },
      { label: 'Indirect association', score: 2 },
      { label: 'Declared PEP, no issues', score: 3 },
      { label: 'Undisclosed PEP', score: 4 },
      { label: 'Procurement conflict or corruption', score: 5 },
    ],
  },
  {
    title: 'Litigation History',
    category: 'litigation_history',
    weight: 2,
    options: [
      { label: 'No litigation', score: 0 },
      { label: 'Minor civil disputes', score: 2 },
      { label: 'Repeated fiduciary disputes', score: 3 },
      { label: 'Fraud-based civil judgments', score: 5 },
    ],
  },
  {
    title: 'Safeguarding & Conduct',
    category: 'safeguarding_conduct',
    weight: 2,
    options: [
      { label: 'Clean references', score: 0 },
      { label: 'Allegations without finding', score: 3 },
      { label: 'Confirmed misconduct involving vulnerable persons', score: 5 },
    ],
  },
  {
    title: 'Reputational & Media',
    category: 'reputational_media',
    weight: 3,
    options: [
      { label: 'No adverse media', score: 0 },
      { label: 'Isolated minor controversy', score: 2 },
      { label: 'Repeated negative coverage', score: 4 },
      { label: 'Major corruption scandal', score: 5 },
    ],
  },
  {
    title: 'Conflict of Interest',
    category: 'conflict_of_interest',
    weight: 2,
    options: [
      { label: 'No conflict', score: 0 },
      { label: 'Manageable disclosed conflict', score: 3 },
      { label: 'Undisclosed material conflict', score: 5 },
    ],
  },
  {
    title: 'Guarantor Reliability',
    category: 'guarantor_reliability',
    weight: 1,
    options: [
      { label: 'Verified and credible', score: 0 },
      { label: 'Weak financial standing', score: 3 },
      { label: 'False or unverifiable guarantor', score: 5 },
    ],
  },
] as const;

export default function InvestigationDetails() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<TabType>('details');
  const [activeSubTab, setActiveSubTab] = useState<SubTabType>('personal');
  const [showUploadArea, setShowUploadArea] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<string>('');
  const [showSubmitReportModal, setShowSubmitReportModal] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [personalDetailsSection, setPersonalDetailsSection] =
    useState<PersonalDetailsSection>('Personal Identification');
  const [riskChecked, setRiskChecked] = useState<Record<string, boolean>>({});
  const [riskRationale, setRiskRationale] = useState<Record<string, string>>({});
  const [isDragging, setIsDragging] = useState(false);
  const [employerReportSubTab, setEmployerReportSubTab] = useState<EmployerReportSubTab>('risk-score');
  const [uploadDocType, setUploadDocType] = useState<string>('agent_report');
  const [selectedHistoryVersion, setSelectedHistoryVersion] = useState<number | null>(null);
  const [validationNotes, setValidationNotes] = useState('');
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showEmployerModal, setShowEmployerModal] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const currentUser = useAppSelector(selectCurrentUser);
  const isSuperAdmin = currentUser?.role === UserRole.SUPER_ADMIN;
  const isAgent = currentUser?.role === UserRole.AGENT;
  const isEmployer = currentUser?.role === UserRole.EMPLOYER;

  // Get user initials
  const getInitials = (firstName?: string, lastName?: string) => {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'U';
  };

  // Fetch investigation data from API
  const {
    data: investigation,
    isLoading,
    error,
  } = useGetInvestigationByIdQuery(id || '', {
    skip: !id,
  });

  // Fetch available agents for assignment dropdown
  const { data: availableAgents = [] } = useGetAllUsersQuery(
    { role: 'agent' },
    { skip: !isSuperAdmin }
  );

  // Delete document mutation
  const [deleteDocument, { isLoading: isDeleting }] = useDeleteDocumentMutation();

  // Agent assignment mutations
  const [assignAgent, { isLoading: isAssigning }] = useAssignAgentMutation();
  const [reassignAgent, { isLoading: isReassigning }] =
    useReassignInvestigationMutation();
  const [validateReport, { isLoading: isValidating }] =
    useValidateInvestigationReportMutation();

  // Risk assessment queries & mutations (5a)
  const { data: existingRiskAssessment } = useGetRiskAssessmentQuery(id || '', { skip: !id });
  const { data: riskHistory } = useGetRiskAssessmentHistoryQuery(id || '', { skip: !id });
  const [submitRiskAssessment, { isLoading: isSubmittingRisk }] = useSubmitRiskAssessmentMutation();

  // Filtered documents queries (5c)
  const { data: employeeDocs } = useGetFilteredDocumentsQuery(
    { id: id || '', uploadedBy: 'employee' },
    { skip: !id }
  );
  const { data: agentDocs } = useGetFilteredDocumentsQuery(
    { id: id || '', uploadedBy: 'agent' },
    { skip: !id }
  );
  const { data: allDocs } = useGetFilteredDocumentsQuery(
    { id: id || '' },
    { skip: !id || !isSuperAdmin }
  );

  // Agent document upload mutation (5d)
  const [uploadAgentDoc, { isLoading: isUploading }] = useUploadAgentDocumentMutation();

  // Admin document approval mutation (5e)
  const [approveDoc, { isLoading: isApproving }] = useApproveDocumentMutation();

  // Pre-populate risk checkboxes & rationale from existing assessment
  useEffect(() => {
    if (existingRiskAssessment?.categoryScores) {
      const checked: Record<string, boolean> = {};
      const rationale: Record<string, string> = {};
      existingRiskAssessment.categoryScores.forEach((cs) => {
        checked[`${cs.category}::${cs.selectedCriteria}`] = true;
        if (cs.rationale) rationale[cs.category] = cs.rationale;
      });
      setRiskChecked(checked);
      setRiskRationale(rationale);
    }
  }, [existingRiskAssessment]);

  // Format helper functions
  const formatDate = (dateString?: string) => {
    if (!dateString) return '---';
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const calculateAge = (dateOfBirth?: string) => {
    if (!dateOfBirth) return '';
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < birthDate.getDate())
    ) {
      age--;
    }
    return age;
  };

  const getStatusLabel = (status: InvestigationStatus) => {
    const statusLabels: Record<InvestigationStatus, string> = {
      [InvestigationStatus.NEW]: 'New',
      [InvestigationStatus.PENDING]: 'Pending',
      [InvestigationStatus.AWAITING_APPROVAL]: 'Awaiting Approval',
      [InvestigationStatus.IN_PROGRESS]: 'In Progress',
      [InvestigationStatus.UNDER_REVIEW]: 'Under Review',
      [InvestigationStatus.COMPLETED]: 'Completed',
      [InvestigationStatus.CANCELLED]: 'Cancelled',
    };
    return statusLabels[status] || status;
  };

  const getStatusColor = (status: InvestigationStatus) => {
    switch (status) {
      case InvestigationStatus.COMPLETED:
        return 'bg-green-50 text-green-700 border-green-100';
      case InvestigationStatus.IN_PROGRESS:
        return 'bg-blue-50 text-blue-700 border-blue-100';
      case InvestigationStatus.UNDER_REVIEW:
        return 'bg-orange-50 text-orange-700 border-orange-100';
      case InvestigationStatus.PENDING:
      case InvestigationStatus.AWAITING_APPROVAL:
        return 'bg-yellow-50 text-yellow-700 border-yellow-100';
      case InvestigationStatus.NEW:
        return 'bg-purple-50 text-purple-700 border-purple-100';
      case InvestigationStatus.CANCELLED:
        return 'bg-red-50 text-red-700 border-red-100';
      default:
        return 'bg-gray-50 text-gray-700 border-gray-100';
    }
  };

  const getDocStatusBadge = (docStatus?: string) => {
    const status = docStatus || (investigation ? getStatusLabel(investigation.status) : '');
    const lower = status.toLowerCase();
    if (lower.includes('completed') || lower.includes('approved')) {
      return { label: 'Completed', className: 'bg-green-50 text-green-700 border-green-200' };
    }
    if (lower.includes('rejected') || lower.includes('cancelled')) {
      return { label: 'Rejected', className: 'bg-red-50 text-red-700 border-red-200' };
    }
    return { label: 'Awaiting Approval', className: 'bg-orange-50 text-orange-700 border-orange-200' };
  };

  if (isLoading) {
    return (
      <main className="w-full h-screen flex items-center justify-center bg-[#EDEFF3]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-gray-200 border-t-gray-900 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading investigation details...</p>
        </div>
      </main>
    );
  }

  if (error || !investigation) {
    return (
      <main className="w-full h-screen flex items-center justify-center bg-[#EDEFF3]">
        <div className="text-center">
          <p className="text-red-600 mb-4">Failed to load investigation details</p>
          <button
            onClick={() => navigate('/investigations')}
            className="px-6 py-2 bg-gray-900 text-white rounded-xl hover:bg-gray-800"
          >
            Back to Investigations
          </button>
        </div>
      </main>
    );
  }

  const employeeName = `${investigation.employeeFirstName} ${investigation.employeeMiddleName ? investigation.employeeMiddleName + ' ' : ''}${investigation.employeeLastName}`;
  const age = investigation.dateOfBirth
    ? calculateAge(investigation.dateOfBirth)
    : null;

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    const file = files?.[0];
    if (!file || !id) return;
    try {
      await uploadAgentDoc({ id, file, documentType: uploadDocType }).unwrap();
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: any) {
      alert(err?.data?.message || 'Failed to upload document');
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    const file = files?.[0];
    if (!file || !id) return;
    try {
      await uploadAgentDoc({ id, file, documentType: uploadDocType }).unwrap();
    } catch (err: any) {
      alert(err?.data?.message || 'Failed to upload document');
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleViewDocument = (doc: any) => {
    try {
      const fileUrl = doc.downloadUrl || doc.fileUrl;
      if (!fileUrl) {
        alert('Document URL not available');
        return;
      }

      let fullUrl = fileUrl;
      if (!fileUrl.startsWith('http')) {
        const baseUrl =
          import.meta.env.VITE_API_BASE_URL || 'https://checker-api.podus.org';
        fullUrl = `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
      }

      window.open(fullUrl, '_blank');
    } catch (error) {
      console.error('Error viewing document:', error);
      alert('Failed to view document');
    }
  };

  const handleDownloadDocument = async (documentId: string) => {
    if (!id) return;
    try {
      const doc = investigation?.documents?.find((d) => d.id === documentId);
      if (!doc) {
        alert('Document not found');
        return;
      }

      const token = localStorage.getItem('access_token');
      const fileUrl = doc.downloadUrl || doc.fileUrl;

      if (!fileUrl) {
        alert('Document URL not available');
        return;
      }

      let fullUrl = fileUrl;
      if (!fileUrl.startsWith('http')) {
        const baseUrl =
          import.meta.env.VITE_API_BASE_URL || 'https://checker-api.podus.org';
        fullUrl = `${baseUrl}${fileUrl.startsWith('/') ? '' : '/'}${fileUrl}`;
      }

      const response = await fetch(fullUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.fileName || `document-${documentId}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading document:', error);
      alert('Failed to download document. Please try again.');
    }
  };

  const handleDeleteDocument = async (documentId: string) => {
    if (!id || !window.confirm('Are you sure you want to delete this document?'))
      return;

    try {
      await deleteDocument({ investigationId: id, documentId }).unwrap();
      alert('Document deleted successfully');
    } catch (error: any) {
      console.error('Error deleting document:', error);
      alert(parseErrorMessage(error, 'Failed to delete document'));
    }
  };

  const handleAssignAgent = async () => {
    if (!id || !selectedAgentId) return;

    try {
      if (investigation?.agentId) {
        await reassignAgent({
          id,
          data: { agentId: selectedAgentId },
        }).unwrap();
        alert('Agent reassigned successfully');
      } else {
        await assignAgent({ id, agentId: selectedAgentId }).unwrap();
        alert('Agent assigned successfully');
      }
      setShowAssignModal(false);
      setSelectedAgentId('');
    } catch (error: any) {
      console.error('Error assigning agent:', error);
      alert(parseErrorMessage(error, 'Failed to assign agent'));
    }
  };

  const handleValidationSubmit = async (
    approved: boolean,
    validationNotes?: string
  ) => {
    if (!id) return;

    try {
      await validateReport({
        id,
        approved,
        ...(validationNotes && { validationNotes }),
      }).unwrap();

      setShowValidationModal(false);

      const message = approved
        ? 'Investigation approved successfully! Earning created.'
        : 'Investigation rejected. Agent will be notified to revise.';
      alert(message);
    } catch (error: any) {
      console.error('Failed to validate investigation:', error);
      alert(parseErrorMessage(error, 'Failed to validate investigation'));
    }
  };

  const handleSubmitRiskAssessment = async () => {
    if (!id) return;

    // Build one score per category from checked options
    const categoryScores = RISK_ASSESSMENT_CATEGORIES.map((cat) => {
      // Find the checked option for this category
      const selectedOption = cat.options.find(
        (opt) => riskChecked[`${cat.category}::${opt.label}`]
      );
      return {
        category: cat.category,
        rawScore: selectedOption?.score ?? 0,
        selectedCriteria: selectedOption?.label ?? cat.options[0].label,
        rationale: riskRationale[cat.category] || '',
        evidenceReferences: '',
      };
    });

    try {
      await submitRiskAssessment({ id, data: { categoryScores, investigationNotes: '' } }).unwrap();
      alert('Risk assessment submitted successfully');
    } catch (err: any) {
      alert(err?.data?.message || 'Failed to submit risk assessment');
    }
  };

  // ---------- Read-only field component ----------
  const ReadOnlyField = ({
    label,
    value,
  }: {
    label: string;
    value: React.ReactNode;
  }) => (
    <div>
      <p className="text-sm font-semibold text-[#1b1b1b] mb-2">
        {label}
      </p>
      <div className="border-l-[3px] border-[#d9d9d9] pl-4">
        <p className="text-sm font-bold text-[#1b1b1b]">{value || '---'}</p>
      </div>
    </div>
  );

  // ---------- Document card component ----------
  const DocumentCard = ({
    doc,
    showStatusBadge = false,
  }: {
    doc: any;
    showStatusBadge?: boolean;
  }) => {
    const isPDF = doc.mimeType === 'application/pdf';
    const isImage = doc.mimeType?.startsWith('image/');
    const badge = getDocStatusBadge((doc as any).status);

    return (
      <div className="border border-[#e0e0e0] rounded-xl overflow-hidden hover:border-gray-400 transition-colors">
        {/* File preview area */}
        <div className="w-full aspect-[4/3] bg-[#f5f5f5] flex items-center justify-center overflow-hidden relative group">
          {isImage ? (
            <img
              src={
                doc.fileUrl.startsWith('http')
                  ? doc.fileUrl
                  : `${(import.meta.env.VITE_API_BASE_URL || 'https://checker-api.podus.org/api/v1').replace(/\/api\/v1\/?$/, '')}${doc.fileUrl}`
              }
              alt={doc.fileName}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />
          ) : isPDF ? (
            <div className="flex flex-col items-center gap-1">
              <div className="w-12 h-14 bg-red-100 rounded flex items-center justify-center">
                <span className="text-red-600 text-[10px] font-bold">PDF</span>
              </div>
            </div>
          ) : (
            <div className="space-y-1.5 px-6 w-full">
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  className="h-1.5 bg-[#ddd] rounded-full"
                  style={{ width: `${70 + Math.random() * 30}%` }}
                />
              ))}
            </div>
          )}
          {/* Hover overlay */}
          <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <button
              onClick={() => handleViewDocument(doc)}
              className="text-white hover:scale-110 transition-transform"
              title="View document"
            >
              <Eye className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Title + actions */}
        <div className="px-3 py-2.5">
          <p
            className="text-xs font-medium text-[#1b1b1b] truncate mb-2"
            title={doc.fileName}
          >
            {doc.fileName}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => handleViewDocument(doc)}
              className="p-1.5 rounded hover:bg-gray-100 text-[#4e4d4d] hover:text-[#1b1b1b] transition-colors"
              title="View"
            >
              <Eye className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleDownloadDocument(doc.id)}
              className="p-1.5 rounded hover:bg-gray-100 text-[#4e4d4d] hover:text-[#1b1b1b] transition-colors"
              title="Replace"
            >
              <RefreshCw className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={() => handleDeleteDocument(doc.id)}
              disabled={isDeleting}
              className="p-1.5 rounded hover:bg-gray-100 text-[#4e4d4d] hover:text-red-600 transition-colors disabled:opacity-50"
              title="Delete"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
          {showStatusBadge && (
            <div className="mt-2">
              <Badge
                variant="outline"
                className={`text-[10px] px-2 py-0.5 rounded-md font-medium ${badge.className}`}
              >
                {badge.label}
              </Badge>
            </div>
          )}
          {doc.approvalStatus && (
            <span className={`inline-block px-2 py-0.5 text-xs rounded-full mt-1 ${
              doc.approvalStatus === 'approved' || doc.approvalStatus === 'completed' ? 'bg-green-100 text-green-700' :
              doc.approvalStatus === 'rejected' ? 'bg-red-100 text-red-700' :
              'bg-orange-100 text-orange-700'
            }`}>
              {doc.approvalStatus === 'awaiting_approval' ? 'Awaiting Approval' :
               doc.approvalStatus.charAt(0).toUpperCase() + doc.approvalStatus.slice(1)}
            </span>
          )}
          {isSuperAdmin && doc.approvalStatus === 'awaiting_approval' && (
            <div className="flex gap-2 mt-2">
              <button
                onClick={async () => {
                  if (!id) return;
                  try {
                    await approveDoc({ investigationId: id, documentId: doc.id, data: { status: 'approved' } }).unwrap();
                  } catch (err: any) {
                    alert(err?.data?.message || 'Failed to approve');
                  }
                }}
                disabled={isApproving}
                className="px-3 py-1 bg-green-500 text-white text-xs rounded hover:bg-green-600 disabled:opacity-50"
              >
                Approve
              </button>
              <button
                onClick={async () => {
                  if (!id) return;
                  const notes = prompt('Rejection reason:');
                  try {
                    await approveDoc({ investigationId: id, documentId: doc.id, data: { status: 'rejected', ...(notes ? { notes } : {}) } }).unwrap();
                  } catch (err: any) {
                    alert(err?.data?.message || 'Failed to reject');
                  }
                }}
                disabled={isApproving}
                className="px-3 py-1 bg-red-500 text-white text-xs rounded hover:bg-red-600 disabled:opacity-50"
              >
                Reject
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ---------- Personal details sections content ----------
  const renderPersonalDetailsContent = () => {
    switch (personalDetailsSection) {
      case 'Personal Identification': {
        // Parse address history JSON
        let addresses: { date?: string; address?: string }[] = [];
        if (investigation.addressHistory) {
          try {
            addresses = JSON.parse(investigation.addressHistory);
          } catch { /* ignore parse errors */ }
        }

        return (
          <div>
            <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">
              Personal Identification &amp; Verification
            </h3>
            <div className="flex flex-col gap-10">
              <ReadOnlyField
                label="First Name"
                value={investigation.fullLegalName || employeeName}
              />
              <ReadOnlyField
                label="Previous Names (including maiden/aliases):"
                value={investigation.previousNames || investigation.employeeMiddleName || '---'}
              />
              <ReadOnlyField
                label="Date of Birth:"
                value={
                  investigation.dateOfBirth
                    ? `${formatDate(investigation.dateOfBirth)}${age ? ` - ${age}yrs Old` : ''}`
                    : '---'
                }
              />
              <ReadOnlyField
                label="Place of Birth (State & LGA):"
                value={investigation.placeOfBirth || (investigation.localGovernmentArea ? `${investigation.localGovernmentArea}, ${investigation.stateOfOrigin || ''}` : '---')}
              />
              <ReadOnlyField
                label="Other Nationalities (if any):"
                value={investigation.otherNationalities || '---'}
              />
              <ReadOnlyField
                label="National Identification Number (NIN):"
                value={investigation.nationalIdentificationNumber || '---'}
              />
              <ReadOnlyField
                label="National Identification Number (NIN):"
                value={investigation.nationalIdentificationNumber || '---'}
              />

              {/* Address History */}
              <div>
                <p className="text-sm font-semibold text-[#1b1b1b] mb-2">
                  Address History (Past 10 Years) Provide full residential addresses with dates:
                </p>
                <div className="border-l-[3px] border-[#d9d9d9] pl-4">
                  {addresses.length > 0 ? (
                    <div className="flex flex-col gap-6">
                      {addresses.map((addr, i) => (
                        <div key={i} className="flex flex-col gap-4">
                          <p className="text-sm">
                            <span className="font-normal">Date:</span>{'   '}
                            <span className="font-bold text-[#1b1b1b]">{addr.date || '---'}</span>
                          </p>
                          <p className="text-sm">
                            <span className="font-normal">Address:</span>{'   '}
                            <span className="font-bold text-[#1b1b1b]">{addr.address || '---'}</span>
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="flex flex-col gap-4">
                      <p className="text-sm">
                        <span className="font-normal">Date:</span>{'   '}
                        <span className="font-bold text-[#1b1b1b]">---</span>
                      </p>
                      <p className="text-sm">
                        <span className="font-normal">Address:</span>{'   '}
                        <span className="font-bold text-[#1b1b1b]">
                          {investigation.localGovernmentArea
                            ? `${investigation.localGovernmentArea}, ${investigation.stateOfOrigin || ''}`
                            : '---'}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'Professional Discipline':
        return (
          <div>
            <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">
              Professional Discipline
            </h3>
            <div className="flex flex-col gap-10">
              <ReadOnlyField label="Subject to disciplinary proceedings?" value={investigation.hasDisciplinaryProceedings || '---'} />
              <ReadOnlyField label="Disciplinary Proceedings Details" value={investigation.disciplinaryProceedingsDetails || '---'} />
              <ReadOnlyField label="Professional licence revoked or suspended?" value={investigation.hasLicenceRevoked || '---'} />
              <ReadOnlyField label="Licence Revoked Details" value={investigation.licenceRevokedDetails || '---'} />
              <ReadOnlyField label="Sanctioned by a regulatory body?" value={investigation.hasRegulatoryBodySanction || '---'} />
              <ReadOnlyField label="Regulatory Sanction Details" value={investigation.regulatoryBodySanctionDetails || '---'} />
            </div>
          </div>
        );

      case 'Criminal & Law Enforcement Disclosure':
        return (
          <div>
            <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">
              Criminal &amp; Law Enforcement Disclosure
            </h3>
            <div className="flex flex-col gap-10">
              <ReadOnlyField label="Have you ever been convicted of a criminal offence?" value={investigation.hasConviction || '---'} />
              <ReadOnlyField label="Conviction Details" value={investigation.convictionDetails || '---'} />
              <ReadOnlyField label="Currently under investigation?" value={investigation.isUnderInvestigation || '---'} />
              <ReadOnlyField label="Investigation Details" value={investigation.investigationDetails || '---'} />
              <ReadOnlyField label="Charged with financial crimes?" value={investigation.hasFinancialCrimeCharge || '---'} />
              <ReadOnlyField label="Financial Crime Details" value={investigation.financialCrimeDetails || '---'} />
              <ReadOnlyField label="Entered into a plea bargain?" value={investigation.hasPleaBargain || '---'} />
              <ReadOnlyField label="Plea Bargain Details" value={investigation.pleaBargainDetails || '---'} />
              <ReadOnlyField label="Been disengaged or terminated?" value={investigation.hasBeenDisengaged || '---'} />
              <ReadOnlyField label="Disengagement Details" value={investigation.disengagementDetails || '---'} />
            </div>
          </div>
        );

      case 'Financial & Directorship History':
        return (
          <div>
            <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">
              Financial &amp; Directorship History
            </h3>
            <div className="flex flex-col gap-10">
              <ReadOnlyField label="Director of liquidated company?" value={investigation.hasLiquidatedCompany || '---'} />
              <ReadOnlyField label="Liquidated Company Details" value={investigation.liquidatedCompanyDetails || '---'} />
              <ReadOnlyField label="Disqualified from acting as director?" value={investigation.hasDirectorDisqualification || '---'} />
              <ReadOnlyField label="Disqualification Details" value={investigation.directorDisqualificationDetails || '---'} />
              <ReadOnlyField label="Currently a director/shareholder?" value={investigation.isCurrentDirector || '---'} />
              <ReadOnlyField label="Current Directorship Details" value={investigation.currentDirectorshipDetails || '---'} />
            </div>
          </div>
        );

      case 'Political Exposure':
        return (
          <div>
            <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">
              Political Exposure
            </h3>
            <div className="flex flex-col gap-10">
              <ReadOnlyField label="Politically Exposed Person (PEP)?" value={investigation.isPep || '---'} />
              <ReadOnlyField label="Held any public office?" value={investigation.hasHeldPublicOffice || '---'} />
              <ReadOnlyField label="Public Office Details" value={investigation.publicOfficeDetails || '---'} />
              <ReadOnlyField label="Related to a PEP?" value={investigation.isRelatedToPep || '---'} />
              <ReadOnlyField label="Procurement-related investigation?" value={investigation.hasProcurementInvestigation || '---'} />
            </div>
          </div>
        );

      case 'Workplace Conduct':
        return (
          <div>
            <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">
              Workplace Conduct
            </h3>
            <div className="flex flex-col gap-10">
              <ReadOnlyField label="Dismissed for gross misconduct?" value={investigation.hasMisconductDismissal || '---'} />
              <ReadOnlyField label="Misconduct Details" value={investigation.misconductDismissalDetails || '---'} />
              <ReadOnlyField label="Subject to workplace investigation?" value={investigation.hasWorkplaceInvestigation || '---'} />
              <ReadOnlyField label="Worked with vulnerable groups?" value={investigation.hasWorkedWithVulnerableGroups || '---'} />
              <ReadOnlyField label="Barred from leadership position?" value={investigation.hasLeadershipBar || '---'} />
              <ReadOnlyField label="Leadership Bar Details" value={investigation.leadershipBarDetails || '---'} />
            </div>
          </div>
        );

      case 'Civil Claims':
        return (
          <div>
            <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">Civil Claims</h3>
            <div className="flex flex-col gap-10">
              <ReadOnlyField label="Shareholder/fiduciary disputes?" value={investigation.hasShareholderDisputes || '---'} />
              <ReadOnlyField label="Judgments entered against you?" value={investigation.hasJudgments || '---'} />
            </div>
          </div>
        );

      case 'Media Disclosure':
        return (
          <div>
            <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">
              Media Disclosure
            </h3>
            <div className="flex flex-col gap-10">
              <ReadOnlyField label="Subject of adverse media coverage?" value={investigation.hasAdverseMedia || '---'} />
              <ReadOnlyField label="Adverse Media Details" value={investigation.adverseMediaDetails || '---'} />
              <ReadOnlyField label="Subject to public accusation?" value={investigation.hasPublicAccusation || '---'} />
              <ReadOnlyField label="Undisclosed reputational matters?" value={investigation.hasUndisclosedReputationalMatters || '---'} />
            </div>
          </div>
        );

      case 'Conflict Of Interest':
        return (
          <div>
            <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">
              Conflict Of Interest
            </h3>
            <div className="flex flex-col gap-10">
              <ReadOnlyField label="Business conflict of interest?" value={investigation.hasBusinessConflict || '---'} />
              <ReadOnlyField label="Business Conflict Details" value={investigation.businessConflictDetails || '---'} />
              <ReadOnlyField label="Procurement relationship conflict?" value={investigation.hasProcurementRelationship || '---'} />
              <ReadOnlyField label="Procurement Relationship Details" value={investigation.procurementRelationshipDetails || '---'} />
              <ReadOnlyField label="Sit on competing boards?" value={investigation.hasCompetingBoard || '---'} />
              <ReadOnlyField label="Competing Board Details" value={investigation.competingBoardDetails || '---'} />
            </div>
          </div>
        );

      case 'Data Protection & Consent':
        return (
          <div>
            <h3 className="text-lg font-bold text-[#1b1b1b] mb-8">
              Data Protection &amp; Consent
            </h3>
            <div className="flex flex-col gap-10">
              <ReadOnlyField label="Consent Given" value={investigation.employeeConsentGiven ? `Yes - ${formatDate(investigation.dateConsentGiven)}` : 'Pending'} />
              <ReadOnlyField label="Criminal Background Check" value={investigation.consentCriminalBackground ? 'Agreed' : 'Not agreed'} />
              <ReadOnlyField label="Employment Reference Checks" value={investigation.consentEmploymentReference ? 'Agreed' : 'Not agreed'} />
              <ReadOnlyField label="Guarantor Verification" value={investigation.consentGuarantorReference ? 'Agreed' : 'Not agreed'} />
              <ReadOnlyField label="Address Verification" value={investigation.consentAddressVerification ? 'Agreed' : 'Not agreed'} />
              <ReadOnlyField label="Social Media Review" value={investigation.consentSocialMediaReview ? 'Agreed' : 'Not agreed'} />
              <ReadOnlyField label="Court Record Searches" value={investigation.consentCourtRecordSearches ? 'Agreed' : 'Not agreed'} />
              <ReadOnlyField label="Lifestyle Audit" value={investigation.consentLifestyleAudit ? 'Agreed' : 'Not agreed'} />
              <ReadOnlyField label="Procurement Integrity Checks" value={investigation.consentProcurementIntegrityChecks ? 'Agreed' : 'Not agreed'} />
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // ---------- Employer: Inline personal details ----------
  const renderEmployerPersonalDetails = () => {
    const fields = [
      { label: 'First Name', value: investigation.employeeFirstName },
      { label: 'Middle Name', value: investigation.employeeMiddleName || '---' },
      { label: 'Last Surname', value: investigation.employeeLastName },
      {
        label: 'Date Of Birth',
        value: investigation.dateOfBirth
          ? `${formatDate(investigation.dateOfBirth)}${age ? ` - ${age}yrs Old` : ''}`
          : '---',
      },
      { label: 'Nationality', value: investigation.nationality || '---' },
      { label: 'State of Origin', value: investigation.stateOfOrigin || '---' },
      { label: 'LGA', value: investigation.localGovernmentArea || '---' },
      { label: 'NIN', value: investigation.iin || '---' },
    ];

    return (
      <div className="space-y-6">
        {fields.map((field) => (
          <div key={field.label} className="flex items-baseline gap-2">
            <span className="text-sm text-gray-500">{field.label}:</span>
            <span className="text-sm font-bold text-[#151515]">
              {field.value || '---'}
            </span>
          </div>
        ))}
      </div>
    );
  };

  // ---------- Employer: Circular Risk Score ----------
  const RiskScoreCircle = ({ score }: { score: number }) => {
    const radius = 54;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (score / 100) * circumference;
    const scoreColor = score <= 40 ? '#EF4444' : score <= 70 ? '#FF6B35' : '#22C55E';

    return (
      <div className="relative w-36 h-36 flex-shrink-0">
        <svg className="w-full h-full -rotate-90" viewBox="0 0 120 120">
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="10"
          />
          <circle
            cx="60"
            cy="60"
            r={radius}
            fill="none"
            stroke={scoreColor}
            strokeWidth="10"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={strokeDashoffset}
            className="transition-all duration-700"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-2xl font-bold" style={{ color: scoreColor }}>
            {score}%
          </span>
        </div>
      </div>
    );
  };

  // ---------- Employer: Risk Assessment Score view (read-only) ----------
  const renderEmployerRiskScore = () => {
    const hasReport = !!investigation.reportUrl;

    if (!hasReport) {
      // Design 5 - Pending / Empty state
      return (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-[#FFF3ED] flex items-center justify-center mb-6">
            <div className="w-6 h-6 rounded-full bg-[#FF6B35]" />
          </div>
          <p className="text-base font-semibold text-[#1b1b1b] mb-1">
            Your Investigation Report is on its way.
          </p>
          <p className="text-sm text-[#4e4d4d]">
            Check back later for the completed report.
          </p>
        </div>
      );
    }

    // Design 3 - Risk Assessment Score with circular ring + read-only checkboxes
    const riskData = existingRiskAssessment;
    const riskPercent = riskData?.percentage ?? investigation.riskScorePercentage ?? 0;
    const riskLevelText = riskData?.riskLevel ?? investigation.riskLevel ?? '';

    // Build a set of selected keys from API data if available
    const apiSelectedKeys = new Set<string>();
    if (riskData?.categoryScores) {
      riskData.categoryScores.forEach((cs: any) => {
        apiSelectedKeys.add(`${cs.category}::${cs.selectedCriteria}`);
      });
    }

    return (
      <div>
        {/* Risk Score header with circle */}
        <div className="flex items-start gap-6 mb-10">
          <RiskScoreCircle score={riskPercent} />
          <div className="flex-1">
            <h3 className="text-lg font-bold text-[#1b1b1b] mb-2">Risk Score</h3>
            {riskLevelText && (
              <p className="text-sm font-medium text-[#1b1b1b] mb-1">Level: {riskLevelText}</p>
            )}
            <p className="text-sm text-[#4e4d4d] leading-relaxed">
              This risk score reflects the overall assessment of the background
              investigation. It considers personal identification verification,
              criminal checks, financial history, and other relevant factors to
              provide a comprehensive risk profile.
            </p>
            {riskData?.version && (
              <p className="text-xs text-[#999] mt-2">
                Assessment version {riskData.version}
                {investigation.lastRiskAssessmentAt && (
                  <> &middot; Last assessed {new Date(investigation.lastRiskAssessmentAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}</>
                )}
              </p>
            )}
          </div>
        </div>

        {/* Read-only risk assessment categories */}
        <div className="space-y-8">
          {RISK_ASSESSMENT_CATEGORIES.map((category) => (
            <div key={category.title}>
              <h4 className="text-sm font-bold text-[#1b1b1b] mb-3">
                {category.title}
              </h4>
              <div className="space-y-2.5 pl-1">
                {category.options.map((option) => {
                  const key = `${category.category}::${option.label}`;
                  const isChecked = riskData?.categoryScores
                    ? apiSelectedKeys.has(key)
                    : riskChecked[key] || false;
                  const isPositive = option.score === 0;

                  return (
                    <div key={key} className="flex items-center gap-3">
                      <div
                        className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isChecked
                            ? isPositive
                              ? 'bg-green-500 border-green-500'
                              : 'bg-[#1b1b1b] border-[#1b1b1b]'
                            : 'border-[#ccc]'
                        }`}
                      >
                        {isChecked && (
                          <svg
                            className="w-2.5 h-2.5 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={3}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm text-[#4e4d4d]">{option.label}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // ---------- Employer: Investigation Field Report view ----------
  const renderEmployerFieldReport = () => {
    // Use agentDocs filtered for investigation_report type
    const reportDocs = agentDocs?.filter((d: any) => d.documentType === 'investigation_report') || [];

    if (!investigation.reportUrl && reportDocs.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-full bg-[#FFF3ED] flex items-center justify-center mb-6">
            <div className="w-6 h-6 rounded-full bg-[#FF6B35]" />
          </div>
          <p className="text-base font-semibold text-[#1b1b1b] mb-1">
            Your Investigation Report is on its way.
          </p>
          <p className="text-sm text-[#4e4d4d]">
            Check back later for the completed report.
          </p>
        </div>
      );
    }

    return (
      <div>
        {/* Agent-uploaded investigation report documents */}
        {reportDocs.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {reportDocs.map((doc: any) => (
              <DocumentCard key={doc.id} doc={doc} showStatusBadge />
            ))}
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <div className="border border-[#e0e0e0] rounded-xl overflow-hidden hover:border-gray-400 transition-colors">
            {/* File preview placeholder */}
            <div className="w-full aspect-[4/3] bg-[#f5f5f5] flex items-center justify-center relative group">
              <div className="space-y-1.5 px-6 w-full">
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="h-1.5 bg-[#ddd] rounded-full"
                    style={{ width: `${70 + Math.random() * 30}%` }}
                  />
                ))}
              </div>
              <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                <button
                  onClick={() => window.open(investigation.reportUrl, '_blank')}
                  className="text-white hover:scale-110 transition-transform"
                  title="View report"
                >
                  <Eye className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Title + actions */}
            <div className="px-3 py-2.5">
              <p className="text-xs font-medium text-[#1b1b1b] truncate mb-2">
                Investigation Report
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => window.open(investigation.reportUrl, '_blank')}
                  className="p-1.5 rounded hover:bg-gray-100 text-[#4e4d4d] hover:text-[#1b1b1b] transition-colors"
                  title="View"
                >
                  <Eye className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={async () => {
                    if (!investigation.reportUrl) return;
                    try {
                      const token = localStorage.getItem('access_token');
                      const response = await fetch(investigation.reportUrl, {
                        method: 'GET',
                        headers: { Authorization: `Bearer ${token}` },
                      });
                      if (!response.ok) throw new Error('Failed to download report');
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `investigation-report-${investigation.id}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      window.URL.revokeObjectURL(url);
                      document.body.removeChild(a);
                    } catch (err) {
                      console.error('Error downloading report:', err);
                      alert('Failed to download report. Please try again.');
                    }
                  }}
                  className="p-1.5 rounded hover:bg-gray-100 text-[#4e4d4d] hover:text-[#1b1b1b] transition-colors"
                  title="Download"
                >
                  <Download className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <main className="w-full min-h-screen lg:h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
      <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
        {/* Header/Navbar */}
        <PodusCheckerNavigation
          showTitle={false}
          showInfo={false}
          showLocation={false}
          notificationCount={3}
          userName={
            currentUser
              ? `${currentUser.firstName} ${currentUser.lastName}`
              : 'User'
          }
          userInitials={
            currentUser
              ? getInitials(currentUser.firstName, currentUser.lastName)
              : 'U'
          }
          only={true}
          className="border-b border-gray-100"
        />

        {/* Content */}
        <div className="flex-1 overflow-hidden p-4 md:p-8 lg:p-10 flex flex-col">
          {/* Back Button + Employee Name + Action Buttons */}
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <button
              onClick={() => navigate(-1)}
              className="flex items-center justify-center w-9 h-9 rounded-full bg-[#f0f0f0] hover:bg-[#e0e0e0] transition-colors"
            >
              <ChevronLeft className="w-5 h-5 text-[#1b1b1b]" />
            </button>
            <h1 className="text-xl font-bold text-[#1b1b1b]">{employeeName}</h1>

            {isSuperAdmin && (
              <div className="flex flex-wrap items-center gap-2 md:gap-3 ml-auto">
                {investigation.status === InvestigationStatus.UNDER_REVIEW && (
                  <Button
                    onClick={() => setShowReviewModal(true)}
                    className="bg-amber-500 hover:bg-amber-600 text-white px-3 md:px-5 py-2 md:py-2.5 rounded-xl text-xs md:text-sm font-medium flex items-center gap-2"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    Review
                  </Button>
                )}
                {investigation.employer && (
                  <Button
                    onClick={() => setShowEmployerModal(true)}
                    variant="outline"
                    className="border-[#e0e0e0] text-[#1b1b1b] hover:bg-gray-50 px-3 md:px-5 py-2 md:py-2.5 rounded-xl text-xs md:text-sm font-medium"
                  >
                    Employer Details
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Alert Banner */}
          <div className="flex items-start gap-2.5 mb-6 py-2">
            <div className="w-5 h-5 rounded-full bg-[#FF6600] mt-0.5 flex-shrink-0 flex items-center justify-center">
              <span className="text-white text-[10px] font-bold">!</span>
            </div>
            <p className="text-sm text-[#747474] leading-relaxed">
              This page displays comprehensive details about the background check
              investigation, including employee information, investigation status,
              assigned agent, uploaded documents, and investigation reports. Track the
              progress and review all submitted materials in one place.
            </p>
          </div>

          {/* Two-column layout: left sidebar + content */}
          <div className="flex flex-col lg:flex-row gap-6 flex-1 min-h-0">
            {/* Left Sidebar - Request Details */}
            <div className="lg:w-80 flex-shrink-0 overflow-y-auto">
              <div className="bg-[#f5f5f5] rounded-lg p-4 md:p-6">
                <h2 className="text-xl font-bold text-[#181d27] mb-6">
                  Request Details
                </h2>

                <div className="space-y-6">
                  <p className="text-sm text-[#717680]">
                    Employee Name:{' '}
                    <span className="font-bold text-[#252b37]">{employeeName}</span>
                  </p>

                  <div className="flex items-center gap-2.5">
                    <span className="text-sm text-[#717680]">Check Type:</span>
                    <Badge
                      variant="outline"
                      className={`text-[13px] px-2.5 py-1 rounded font-medium capitalize border-0 ${
                        investigation.checkType === CheckType.STANDARD
                          ? 'bg-[rgba(52,72,240,0.12)] text-[#3448f0]'
                          : 'bg-orange-50 text-[#FF6B35]'
                      }`}
                    >
                      {investigation.checkType}
                    </Badge>
                  </div>

                  {/* Show Check By in report tab */}
                  {activeTab === 'report' && investigation.agent && (
                    <p className="text-sm text-[#717680]">
                      Checked By:{' '}
                      <span className="font-bold text-[#252b37]">
                        {investigation.agent.firstName} {investigation.agent.lastName}
                      </span>
                    </p>
                  )}

                  <p className="text-sm text-[#717680]">
                    Date Requested:{' '}
                    <span className="font-bold text-[#252b37]">
                      {formatDate(investigation.dateRequested || investigation.createdAt)}
                    </span>
                  </p>

                  <p className="text-sm text-[#717680]">
                    Report Submitted:{' '}
                    <span className="font-bold text-[#252b37]">
                      {formatDate(investigation.reportSubmittedDate)}
                    </span>
                  </p>

                  <div className="flex items-center gap-2.5">
                    <span className="text-sm text-[#717680]">Status:</span>
                    <Badge
                      variant="outline"
                      className={`text-[13px] px-2.5 py-1 rounded font-medium border-0 ${getStatusColor(investigation.status)}`}
                    >
                      {getStatusLabel(investigation.status)}
                    </Badge>
                  </div>

                  <p className="text-sm text-[#717680]">
                    Amount Paid:{' '}
                    <span className="font-bold text-[#252b37]">
                      {investigation.isPaid && investigation.amountPaid
                        ? new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(investigation.amountPaid)
                        : investigation.checkType === CheckType.ENHANCED
                          ? '\u20A678,900'
                          : '\u20A656,900'}
                    </span>
                  </p>

                  {/* Validation Button for Super Admin */}
                  {isSuperAdmin &&
                    investigation.status === InvestigationStatus.UNDER_REVIEW && (
                      <div className="pt-1">
                        <Button
                          onClick={() => setShowValidationModal(true)}
                          disabled={isValidating}
                          className="w-full bg-[#1b1b1b] hover:bg-[#333] text-white px-3 py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5"
                        >
                          <CheckCircle2 className="w-3.5 h-3.5" />
                          Validate Investigation
                        </Button>
                      </div>
                    )}

                  {/* Assign Agent Button for Super Admin */}
                  {isSuperAdmin && (
                    <div className="pt-1">
                      <Button
                        onClick={() => setShowAssignModal(true)}
                        variant="outline"
                        className="w-full border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35]/10 px-3 py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5"
                      >
                        <UserPlus className="w-3.5 h-3.5" />
                        {investigation.agent ? 'Reassign Agent' : 'Assign Agent'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Right Content Area */}
            <div className="flex-1 min-w-0 flex flex-col min-h-0 overflow-hidden">
              {/* Main Toggle Buttons */}
              <div className="flex flex-wrap gap-2 md:gap-4 mb-4 md:mb-6">
                <button
                  onClick={() => {
                    setActiveTab('details');
                    setActiveSubTab('personal');
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                    activeTab === 'details'
                      ? 'bg-[#151515] text-white'
                      : 'bg-white text-[#151515] border border-[#e0e0e0] hover:bg-gray-50'
                  }`}
                >
                  Details Submitted
                </button>
                <button
                  onClick={() => {
                    setActiveTab('report');
                    setActiveSubTab('personal');
                    if (isEmployer) setEmployerReportSubTab('risk-score');
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-colors ${
                    activeTab === 'report'
                      ? 'bg-[#151515] text-white'
                      : 'bg-white text-[#151515] border border-[#e0e0e0] hover:bg-gray-50'
                  }`}
                >
                  {isAgent ? 'Submit Investigation Report' : 'Investigation Report'}
                </button>

                {/* Submit Report button for agents */}
                {isAgent &&
                  !isEmployer &&
                  activeTab === 'report' &&
                  investigation.status === InvestigationStatus.IN_PROGRESS && (
                    <Button
                      onClick={() => setShowSubmitReportModal(true)}
                      className="ml-auto bg-[#FF6B35] hover:bg-[#FF5722] text-white px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                    >
                      <Plus className="w-4 h-4" />
                      {investigation.reportUrl ? 'Resubmit Report' : 'Submit Report'}
                    </Button>
                  )}
              </div>

              {/* ============ DETAILS SUBMITTED TAB ============ */}
              {activeTab === 'details' && (
                <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                  {/* Sub-tabs: Personal Details | Documents */}
                  <div className="relative mb-6">
                    <div className="flex gap-10">
                      <button
                        onClick={() => {
                          setActiveSubTab('personal');
                          setShowUploadArea(false);
                        }}
                        className={`pb-3 text-sm font-medium transition-colors relative ${
                          activeSubTab === 'personal'
                            ? 'text-[#1b1b1b] font-bold'
                            : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                        }`}
                      >
                        Personal Details
                        {activeSubTab === 'personal' && (
                          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#1b1b1b]" />
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setActiveSubTab('documents');
                          setShowUploadArea(false);
                        }}
                        className={`pb-3 text-sm font-medium transition-colors relative ${
                          activeSubTab === 'documents'
                            ? 'text-[#1b1b1b] font-bold'
                            : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                        }`}
                      >
                        Documents
                        {activeSubTab === 'documents' && (
                          <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#1b1b1b]" />
                        )}
                      </button>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#e0e0e0]" />
                  </div>

                  {/* Personal Details Content */}
                  {activeSubTab === 'personal' && (
                    <div className="flex flex-col flex-1 min-h-0">
                      {isEmployer ? (
                        /* Employer: Simple inline label-value pairs */
                        <div className="py-2 flex-1 overflow-y-auto">
                          {renderEmployerPersonalDetails()}
                        </div>
                      ) : (
                        /* Agent / Super Admin: Bordered sections with tab pills */
                        <div className="flex flex-col flex-1 min-h-0">
                          {/* Left vertical progress bar + content */}
                          <div className="flex gap-4 flex-1 min-h-0 overflow-y-auto pr-2">
                            {/* Progress track */}
                            <div className="w-1 bg-[#efefef] rounded-[56px] flex-shrink-0 relative">
                              <div
                                className="absolute top-0 left-0 w-full bg-[#474747] rounded-[56px] transition-all duration-300"
                                style={{
                                  height: `${((DETAILS_SECTION_TABS.indexOf(personalDetailsSection) + 1) / DETAILS_SECTION_TABS.length) * 100}%`,
                                }}
                              />
                            </div>

                            {/* Section content */}
                            <div className="flex-1 min-w-0">
                              {renderPersonalDetailsContent()}
                            </div>
                          </div>

                          {/* Bottom tab pills */}
                          <div className="mt-4 pt-4 flex-shrink-0">
                            <div className="flex flex-wrap gap-2 md:gap-4">
                              {DETAILS_SECTION_TABS.map((tab) => (
                                <button
                                  key={tab}
                                  onClick={() => setPersonalDetailsSection(tab)}
                                  className={`px-4 py-3 rounded-full text-xs font-normal transition-colors whitespace-nowrap ${
                                    personalDetailsSection === tab
                                      ? 'bg-[#1b1b1b] text-white'
                                      : 'bg-[#dcdcdc] text-[#1b1b1b] hover:bg-[#ccc]'
                                  }`}
                                >
                                  {tab}
                                </button>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Documents Content */}
                  {activeSubTab === 'documents' && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                      {(employeeDocs && employeeDocs.length > 0) ? (
                        employeeDocs.map((doc: any) => (
                          <DocumentCard key={doc.id} doc={doc} />
                        ))
                      ) : investigation.documents &&
                      investigation.documents.length > 0 ? (
                        investigation.documents.map((doc) => (
                          <DocumentCard key={doc.id} doc={doc} />
                        ))
                      ) : (
                        <div className="col-span-full text-center py-12">
                          <p className="text-[#4e4d4d]">
                            No documents uploaded yet
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ============ INVESTIGATION REPORT TAB ============ */}
              {activeTab === 'report' && (
                <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                  {isEmployer ? (
                    /* Employer: "Risk Assessment Score" | "Investigation Field Report" sub-tabs */
                    <div className="flex gap-6 mb-6 border-b border-[#e0e0e0]">
                      <button
                        onClick={() => setEmployerReportSubTab('risk-score')}
                        className={`pb-3 text-sm font-medium transition-colors relative ${
                          employerReportSubTab === 'risk-score'
                            ? 'text-[#1b1b1b]'
                            : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                        }`}
                      >
                        Risk Assessment Score
                        {employerReportSubTab === 'risk-score' && (
                          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1b1b1b]" />
                        )}
                      </button>
                      <button
                        onClick={() => setEmployerReportSubTab('field-report')}
                        className={`pb-3 text-sm font-medium transition-colors relative ${
                          employerReportSubTab === 'field-report'
                            ? 'text-[#1b1b1b]'
                            : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                        }`}
                      >
                        Investigation Field Report
                        {employerReportSubTab === 'field-report' && (
                          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1b1b1b]" />
                        )}
                      </button>
                    </div>
                  ) : (
                    /* Agent / Super Admin: "Risk Assessment" | "Documents" sub-tabs */
                    <div className="flex gap-6 mb-6 border-b border-[#e0e0e0]">
                      <button
                        onClick={() => {
                          setActiveSubTab('personal');
                          setShowUploadArea(false);
                        }}
                        className={`pb-3 text-sm font-medium transition-colors relative ${
                          activeSubTab === 'personal'
                            ? 'text-[#1b1b1b]'
                            : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                        }`}
                      >
                        Risk Assessment
                        {activeSubTab === 'personal' && (
                          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1b1b1b]" />
                        )}
                      </button>
                      <button
                        onClick={() => {
                          setActiveSubTab('documents');
                          setShowUploadArea(false);
                        }}
                        className={`pb-3 text-sm font-medium transition-colors relative ${
                          activeSubTab === 'documents'
                            ? 'text-[#1b1b1b]'
                            : 'text-[#4e4d4d] hover:text-[#1b1b1b]'
                        }`}
                      >
                        Documents
                        {activeSubTab === 'documents' && (
                          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#1b1b1b]" />
                        )}
                      </button>
                    </div>
                  )}

                  {/* Employer Report Content */}
                  {isEmployer && employerReportSubTab === 'risk-score' && (
                    <div className="flex-1 overflow-y-auto min-h-0">{renderEmployerRiskScore()}</div>
                  )}
                  {isEmployer && employerReportSubTab === 'field-report' && (
                    <div className="flex-1 overflow-y-auto min-h-0">{renderEmployerFieldReport()}</div>
                  )}

                  {/* Risk Assessment Content (Agent / Super Admin) */}
                  {!isEmployer && activeSubTab === 'personal' && (
                    <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
                      <p className="text-sm text-[#4e4d4d] leading-relaxed mb-4">
                        Review the risk assessment findings below. Each category
                        represents a distinct area of the background investigation.
                        Select the applicable findings for each category based on
                        your investigation results.
                      </p>

                      {/* Version info & history selector */}
                      {(existingRiskAssessment?.version || (riskHistory && riskHistory.length > 0)) && (
                        <div className="flex items-center gap-4 mb-6 flex-shrink-0">
                          {existingRiskAssessment?.version && (
                            <div className="flex items-center gap-2 text-xs text-[#4e4d4d] bg-[#f5f5f5] rounded-lg px-3 py-2">
                              <span>Current: <span className="font-semibold text-[#1b1b1b]">v{existingRiskAssessment.version}</span></span>
                              <span className="text-[#ccc]">|</span>
                              <span className={`font-medium capitalize ${
                                existingRiskAssessment.riskLevel === 'low' ? 'text-green-600' :
                                existingRiskAssessment.riskLevel === 'moderate' ? 'text-yellow-600' :
                                existingRiskAssessment.riskLevel === 'high' ? 'text-orange-600' :
                                'text-red-600'
                              }`}>
                                {existingRiskAssessment.riskLevel} risk
                              </span>
                              <span className="text-[#ccc]">|</span>
                              <span>{existingRiskAssessment.percentage}%</span>
                            </div>
                          )}
                          {riskHistory && riskHistory.length > 1 && (
                            <select
                              value={selectedHistoryVersion ?? ''}
                              onChange={(e) => {
                                const ver = e.target.value ? Number(e.target.value) : null;
                                setSelectedHistoryVersion(ver);
                                if (ver !== null) {
                                  const historyEntry = riskHistory.find((h) => h.version === ver);
                                  if (historyEntry) {
                                    const checked: Record<string, boolean> = {};
                                    const rationale: Record<string, string> = {};
                                    historyEntry.categoryScores.forEach((cs) => {
                                      checked[`${cs.category}::${cs.selectedCriteria}`] = true;
                                      if (cs.rationale) rationale[cs.category] = cs.rationale;
                                    });
                                    setRiskChecked(checked);
                                    setRiskRationale(rationale);
                                  }
                                } else if (existingRiskAssessment?.categoryScores) {
                                  const checked: Record<string, boolean> = {};
                                  const rationale: Record<string, string> = {};
                                  existingRiskAssessment.categoryScores.forEach((cs) => {
                                    checked[`${cs.category}::${cs.selectedCriteria}`] = true;
                                    if (cs.rationale) rationale[cs.category] = cs.rationale;
                                  });
                                  setRiskChecked(checked);
                                  setRiskRationale(rationale);
                                }
                              }}
                              className="text-xs border border-[#e0e0e0] rounded-lg px-2 py-1.5 text-[#4e4d4d]"
                            >
                              <option value="">Current (v{existingRiskAssessment?.version})</option>
                              {riskHistory.map((h) => (
                                <option key={h.version} value={h.version}>
                                  Version {h.version} — {h.riskLevel} ({h.percentage}%)
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      )}

                      <div className="space-y-8 flex-1 overflow-y-auto pr-2 min-h-0">
                        {RISK_ASSESSMENT_CATEGORIES.map((category) => (
                          <div key={category.title}>
                            <h4 className="text-sm font-bold text-[#1b1b1b] mb-3 sticky top-0 bg-white py-1 z-10">
                              {category.title}
                            </h4>
                            <div className="space-y-2.5 pl-1">
                              {category.options.map((option) => {
                                const key = `${category.category}::${option.label}`;
                                const isViewingHistory = selectedHistoryVersion !== null;
                                return (
                                  <label
                                    key={key}
                                    className={`flex items-center gap-3 group ${isViewingHistory ? 'cursor-default opacity-75' : 'cursor-pointer'}`}
                                  >
                                    <div
                                      className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                                        riskChecked[key]
                                          ? 'bg-[#1b1b1b] border-[#1b1b1b]'
                                          : 'border-[#ccc] group-hover:border-[#999]'
                                      }`}
                                      onClick={() => {
                                        if (isViewingHistory) return;
                                        setRiskChecked((prev) => ({
                                          ...prev,
                                          [key]: !prev[key],
                                        }));
                                      }}
                                    >
                                      {riskChecked[key] && (
                                        <svg
                                          className="w-2.5 h-2.5 text-white"
                                          fill="none"
                                          viewBox="0 0 24 24"
                                          stroke="currentColor"
                                          strokeWidth={3}
                                        >
                                          <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M5 13l4 4L19 7"
                                          />
                                        </svg>
                                      )}
                                    </div>
                                    <span className="text-sm text-[#4e4d4d] group-hover:text-[#1b1b1b] transition-colors">
                                      {option.label}
                                    </span>
                                  </label>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>

                      {isAgent && investigation.status === InvestigationStatus.IN_PROGRESS && selectedHistoryVersion === null && (
                        <div className="mt-6 flex-shrink-0">
                          <button
                            onClick={handleSubmitRiskAssessment}
                            disabled={isSubmittingRisk}
                            className="bg-[#FF6B35] hover:bg-[#e05a2a] text-white px-6 py-3 rounded-lg text-sm font-medium disabled:opacity-50"
                          >
                            {isSubmittingRisk
                              ? 'Submitting...'
                              : existingRiskAssessment?.version
                                ? 'Resubmit Risk Assessment'
                                : 'Submit Risk Assessment'}
                          </button>
                        </div>
                      )}
                      {selectedHistoryVersion !== null && (
                        <div className="mt-4 flex-shrink-0">
                          <p className="text-xs text-[#999] italic">
                            Viewing version {selectedHistoryVersion} (read-only). Select "Current" to edit.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Report Documents Content (Agent / Super Admin) */}
                  {!isEmployer && activeSubTab === 'documents' && (
                    <div className="flex-1 overflow-y-auto min-h-0">
                      {/* Final Submit Report CTA for agents — shown first */}
                      {isAgent && investigation.status === InvestigationStatus.IN_PROGRESS && (
                        <div className="mb-6">
                          <div className="bg-[#FFF8F4] border border-[#FF6B35]/20 rounded-xl p-5">
                            <h3 className="text-sm font-bold text-[#1b1b1b] mb-1">
                              Ready to submit?
                            </h3>
                            <p className="text-xs text-[#4e4d4d] mb-4">
                              Once you've completed the risk assessment and uploaded all supporting documents, submit your final report for review.
                            </p>
                            <Button
                              onClick={() => setShowSubmitReportModal(true)}
                              className="bg-[#FF6B35] hover:bg-[#FF5722] text-white px-6 py-2.5 rounded-lg text-sm font-medium flex items-center gap-2"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                              {investigation.reportUrl ? 'Resubmit Final Report' : 'Submit Final Report'}
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Status badge */}
                      <div className="mb-4">
                        <Badge
                          variant="outline"
                          className={`text-xs px-3 py-1 rounded-lg font-medium ${getStatusColor(investigation.status)}`}
                        >
                          {getStatusLabel(investigation.status)}
                        </Badge>
                      </div>

                      {/* Report document if exists */}
                      {investigation.reportUrl && (
                        <div className="mb-6">
                          <h3 className="text-sm font-bold text-[#1b1b1b] mb-3">
                            Report Document
                          </h3>
                          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="border border-[#e0e0e0] rounded-xl overflow-hidden">
                              <div className="w-full aspect-[4/3] bg-[#f5f5f5] flex items-center justify-center relative group">
                                <div className="w-12 h-14 bg-red-100 rounded flex items-center justify-center">
                                  <span className="text-red-600 text-[10px] font-bold">
                                    PDF
                                  </span>
                                </div>
                                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                  <button
                                    onClick={() =>
                                      window.open(
                                        investigation.reportUrl,
                                        '_blank'
                                      )
                                    }
                                    className="text-white hover:scale-110 transition-transform"
                                  >
                                    <Eye className="w-6 h-6" />
                                  </button>
                                </div>
                              </div>
                              <div className="px-3 py-2.5">
                                <p className="text-xs font-medium text-[#1b1b1b] truncate mb-2">
                                  Investigation Report
                                </p>
                                <div className="flex items-center gap-1">
                                  <button
                                    onClick={() =>
                                      window.open(
                                        investigation.reportUrl,
                                        '_blank'
                                      )
                                    }
                                    className="p-1.5 rounded hover:bg-gray-100 text-[#4e4d4d] hover:text-[#1b1b1b] transition-colors"
                                    title="View"
                                  >
                                    <Eye className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (!investigation.reportUrl) return;
                                      try {
                                        const token =
                                          localStorage.getItem('access_token');
                                        const response = await fetch(
                                          investigation.reportUrl,
                                          {
                                            method: 'GET',
                                            headers: {
                                              Authorization: `Bearer ${token}`,
                                            },
                                          }
                                        );
                                        if (!response.ok)
                                          throw new Error(
                                            'Failed to download report'
                                          );
                                        const blob = await response.blob();
                                        const url =
                                          window.URL.createObjectURL(blob);
                                        const a = document.createElement('a');
                                        a.href = url;
                                        a.download = `investigation-report-${investigation.id}.pdf`;
                                        document.body.appendChild(a);
                                        a.click();
                                        window.URL.revokeObjectURL(url);
                                        document.body.removeChild(a);
                                      } catch (err) {
                                        console.error(
                                          'Error downloading report:',
                                          err
                                        );
                                        alert(
                                          'Failed to download report. Please try again.'
                                        );
                                      }
                                    }}
                                    className="p-1.5 rounded hover:bg-gray-100 text-[#4e4d4d] hover:text-[#1b1b1b] transition-colors"
                                    title="Download"
                                  >
                                    <Download className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                                {/* Status badge */}
                                <div className="mt-2">
                                  <Badge
                                    variant="outline"
                                    className={`text-[10px] px-2 py-0.5 rounded-md font-medium ${
                                      investigation.status ===
                                      InvestigationStatus.COMPLETED
                                        ? 'bg-green-50 text-green-700 border-green-200'
                                        : investigation.status ===
                                            InvestigationStatus.CANCELLED
                                          ? 'bg-red-50 text-red-700 border-red-200'
                                          : 'bg-orange-50 text-orange-700 border-orange-200'
                                    }`}
                                  >
                                    {investigation.status ===
                                    InvestigationStatus.COMPLETED
                                      ? 'Completed'
                                      : investigation.status ===
                                          InvestigationStatus.CANCELLED
                                        ? 'Rejected'
                                        : 'Awaiting Approval'}
                                  </Badge>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Uploaded Documents section */}
                      <div>
                        <h3 className="text-sm font-bold text-[#1b1b1b] mb-3">
                          Uploaded Documents
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                          {(isSuperAdmin && allDocs && allDocs.length > 0
                            ? allDocs
                            : agentDocs && agentDocs.length > 0
                              ? agentDocs
                              : investigation.documents || []
                          ).map((doc: any) => (
                            <DocumentCard
                              key={doc.id}
                              doc={doc}
                              showStatusBadge
                            />
                          ))}

                          {/* Add Document card */}
                          {isAgent && investigation.status === InvestigationStatus.IN_PROGRESS && (
                            <div
                              className="border-2 border-dashed border-[#e0e0e0] rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-[#FF6B35] hover:bg-[#FFF8F4] transition-colors min-h-[180px]"
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <div className="flex flex-col items-center gap-2 p-4">
                                <div className="w-10 h-10 rounded-full bg-[#f0f0f0] flex items-center justify-center">
                                  <Plus className="w-5 h-5 text-[#4e4d4d]" />
                                </div>
                                <span className="text-xs font-medium text-[#4e4d4d]">Add Document</span>
                                <select
                                  value={uploadDocType}
                                  onChange={(e) => setUploadDocType(e.target.value)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="text-[10px] px-2 py-1 border border-gray-200 rounded text-[#4e4d4d] bg-white mt-1"
                                >
                                  <option value="agent_report">Agent Report</option>
                                  <option value="investigation_report">Investigation Report</option>
                                  <option value="cv">CV</option>
                                  <option value="national_id">National ID</option>
                                  <option value="bvn">BVN</option>
                                  <option value="passport">Passport</option>
                                  <option value="other">Other</option>
                                </select>
                              </div>
                              <input
                                ref={fileInputRef}
                                type="file"
                                onChange={handleFileUpload}
                                className="hidden"
                                accept=".pdf,.docx,.doc,.txt"
                                multiple
                              />
                            </div>
                          )}
                        </div>

                        {!(isSuperAdmin ? allDocs?.length : agentDocs?.length) && !investigation.documents?.length && (
                          <p className="text-sm text-[#4e4d4d] mt-4">
                            {isAgent
                              ? 'No documents uploaded yet. Click "Add Document" to upload supporting files.'
                              : 'No documents have been uploaded for this investigation yet.'}
                          </p>
                        )}
                      </div>

                      {/* Investigation Notes */}
                      {investigation.investigationNotes && (
                        <div className="mt-6">
                          <h3 className="text-sm font-bold text-[#1b1b1b] mb-2">
                            Investigation Notes
                          </h3>
                          <div className="bg-[#f7f7f7] rounded-xl p-4">
                            <p className="text-sm text-[#4e4d4d] leading-relaxed whitespace-pre-wrap">
                              {investigation.investigationNotes}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Agent Assignment Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowAssignModal(false)}
          />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 p-6 z-10">
            <h3 className="text-lg font-bold text-[#1b1b1b] mb-4">
              {investigation?.agentId ? 'Reassign Agent' : 'Assign Agent'}
            </h3>

            <div className="mb-6">
              <label className="block text-xs font-medium text-[#4e4d4d] mb-2">
                Select Agent
              </label>
              <select
                value={selectedAgentId}
                onChange={(e) => setSelectedAgentId(e.target.value)}
                className="w-full px-4 py-2.5 border border-[#e0e0e0] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FF6B35]/20 focus:border-[#FF6B35]"
              >
                <option value="">Choose an agent...</option>
                {availableAgents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.firstName} {agent.lastName}{' '}
                    {agent.id === investigation?.agentId && '(Current)'}
                  </option>
                ))}
              </select>
              {availableAgents.length === 0 && (
                <p className="text-xs text-red-600 mt-2">
                  No active agents available
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowAssignModal(false);
                  setSelectedAgentId('');
                }}
                variant="outline"
                className="flex-1"
                disabled={isAssigning || isReassigning}
              >
                Cancel
              </Button>
              <Button
                onClick={handleAssignAgent}
                className="flex-1 bg-[#FF6B35] hover:bg-[#FF5722] text-white"
                disabled={!selectedAgentId || isAssigning || isReassigning}
              >
                {isAssigning || isReassigning
                  ? 'Assigning...'
                  : investigation?.agentId
                    ? 'Reassign'
                    : 'Assign'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Report Modal */}
      {id && (
        <SubmitReportModal
          isOpen={showSubmitReportModal}
          onClose={() => setShowSubmitReportModal(false)}
          investigationId={id}
          reportUrl={agentDocs?.[0]?.fileUrl || investigation?.reportUrl || ''}
        />
      )}

      {/* Validation Modal */}
      <ValidationModal
        isOpen={showValidationModal}
        onClose={() => setShowValidationModal(false)}
        onSubmit={handleValidationSubmit}
        isLoading={isValidating}
        investigationId={investigation?.id || ''}
        employeeName={employeeName}
      />

      {/* Review Investigation Modal */}
      {showReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowReviewModal(false)} />
          <div className="relative z-10 w-full max-w-md bg-white rounded-2xl shadow-2xl mx-4 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-5 pb-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Review Investigation</h2>
                  <p className="text-xs text-gray-500">Approve or reject with feedback</p>
                </div>
              </div>
              <button onClick={() => setShowReviewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <XCircle className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="p-5">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-xs text-amber-800">
                  <span className="font-semibold">{employeeName}</span> — Status: <span className="font-semibold">Under Review</span>
                </p>
              </div>

              <label className="block text-sm font-medium text-gray-700 mb-1.5">
                Review Notes <span className="text-xs text-gray-400">(required for rejection)</span>
              </label>
              <textarea
                value={validationNotes}
                onChange={(e) => setValidationNotes(e.target.value)}
                rows={4}
                className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none"
                placeholder="Add notes about your review decision..."
              />
            </div>

            <div className="flex gap-3 p-5 pt-0">
              <Button
                onClick={async () => {
                  await handleValidationSubmit(true, validationNotes || undefined);
                  setValidationNotes('');
                  setShowReviewModal(false);
                }}
                disabled={isValidating}
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              >
                <CheckCircle2 className="w-4 h-4" />
                {isValidating ? 'Processing...' : 'Approve'}
              </Button>
              <Button
                onClick={async () => {
                  if (!validationNotes?.trim()) {
                    alert('Please provide a reason for rejection.');
                    return;
                  }
                  await handleValidationSubmit(false, validationNotes);
                  setValidationNotes('');
                  setShowReviewModal(false);
                }}
                disabled={isValidating}
                variant="outline"
                className="flex-1 border-red-300 text-red-600 hover:bg-red-50 py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
              >
                <XCircle className="w-4 h-4" />
                {isValidating ? 'Processing...' : 'Reject'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Employer Details Modal */}
      {showEmployerModal && investigation?.employer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowEmployerModal(false)} />
          <div className="relative z-10 w-full max-w-sm bg-white rounded-2xl shadow-2xl mx-4 animate-in zoom-in-95 duration-200">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="text-lg font-bold text-gray-900">Employer Details</h2>
              <button onClick={() => setShowEmployerModal(false)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <XCircle className="w-4 h-4 text-gray-500" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <p className="text-[10px] uppercase tracking-wide text-[#4e4d4d] mb-0.5">Company Name</p>
                <p className="text-sm font-medium text-[#1b1b1b]">{investigation.employer.companyName || '---'}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-[#4e4d4d] mb-0.5">Contact Person</p>
                <p className="text-sm font-medium text-[#1b1b1b]">{investigation.employer.firstName} {investigation.employer.lastName}</p>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wide text-[#4e4d4d] mb-0.5">Email</p>
                <p className="text-sm font-medium text-[#1b1b1b]">{investigation.employer.email}</p>
              </div>
              {investigation.employer.phoneNumber && (
                <div>
                  <p className="text-[10px] uppercase tracking-wide text-[#4e4d4d] mb-0.5">Phone</p>
                  <p className="text-sm font-medium text-[#1b1b1b]">{investigation.employer.phoneNumber}</p>
                </div>
              )}
            </div>
            <div className="p-5 pt-0">
              <Button
                onClick={() => setShowEmployerModal(false)}
                variant="outline"
                className="w-full py-2.5 rounded-lg text-sm"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
